import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { researchosService } from '../../services/researchosService';

// Superuser-only extraction studio. Run the DeepSeek pipeline on ONE transcript → review the
// claims / stance / tensions / diligence / entities + cost + grounding → then EXPLICITLY save.
// Nothing lands in the DB until "Save to DB" is clicked; the preview lives only in cache.
const GOLD = 'rgb(var(--brand-gold-rgb))';
const SPK_COLORS = ['#5fbf8f', '#6ea8d8', '#d8a24a', '#c9a0d8', '#e0705f', '#8b95a6'];

// Per-entity stance (bullish/bearish/neutral) — the new Phase-0 signal on topics, entities & claims.
const STANCE = { bullish: { c: '#16a34a', m: '▲' }, bearish: { c: '#dc2626', m: '▼' }, neutral: { c: '#9ca3af', m: '–' } };
const stanceOf = (s) => STANCE[s] || STANCE.neutral;
// Small chip: ▲/▼ + ticker(or name), colored by stance; title carries relevance/listing.
function StanceChip({ e }) {
  const st = stanceOf(e.stance);
  return (
    <span className="inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px]"
      style={{ borderColor: st.c + '66', color: st.c }}
      title={`${e.stance || 'neutral'}${e.relevance ? ' · relevance ' + e.relevance : ''}${e.ticker ? ' · ' + e.ticker : (e.listing === 'not_covered' ? ' · not covered' : '')}`}>
      {st.m} {e.ticker || e.name || e.entity}
    </span>
  );
}

const stanceStyle = {
  asserts: { t: 'ASSERTS', c: '#e9e7e1' }, agrees: { t: 'AGREES', c: '#5fbf8f' },
  disputes: { t: 'DISPUTES', c: '#e0705f' }, deflects: { t: 'DEFLECTS', c: '#d8a24a' },
  silent: { t: '—', c: '#726f68' }, none: { t: '—', c: '#726f68' },
};
const verdictStyle = {
  SUPPORTED: '#5fbf8f', PARTIAL: '#d8a24a', UNSUPPORTED: '#e0705f',
  MISATTRIBUTED: '#e0705f', MODALITY_ERROR: '#e0705f',
};

function Pill({ children, color, solid }) {
  return (
    <span className="rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider"
      style={solid ? { background: color || GOLD, color: '#111' }
        : { border: `1px solid ${color || '#4a4a4a'}`, color: color || '#a7a49b' }}>
      {children}
    </span>
  );
}

// sourceType: 'youtube' | 'x' — the page the studio sits on scopes the document picker and the bulk run to
// its own source (wish #6: X documents preview/commit here exactly like transcripts).
export default function ExtractionStudio({ sourceType } = {}) {
  const [docs, setDocs] = useState([]);
  const [docId, setDocId] = useState('');
  const [job, setJob] = useState(null);        // {job_id}
  const [poll, setPoll] = useState(null);      // cache blob {status, result, log, error}
  const [tab, setTab] = useState('claims');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(null);
  const [spkFilter, setSpkFilter] = useState(null);
  const [showAllEnts, setShowAllEnts] = useState(false);   // entities tab: companies-only (default) vs all
  const timer = useRef(null);
  // ── bulk extract (earliest-first, grounding-gated, no per-video review) ──
  const [bulkCount, setBulkCount] = useState(50);
  const [bulkOrder, setBulkOrder] = useState('earliest');
  const [bulkRedo, setBulkRedo] = useState(false);          // re-extract ALREADY-committed docs too (new version; read-latest)
  const [bulkJob, setBulkJob] = useState(null);            // {job_id, doc_count}
  const [bulkPoll, setBulkPoll] = useState(null);          // cache blob {status, total, done, committed, …}
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkErr, setBulkErr] = useState(null);
  const bulkTimer = useRef(null);
  // ── saved / committed extractions (view any saved one with the full preview UI) ──
  const [savedList, setSavedList] = useState([]);
  const [viewingSaved, setViewingSaved] = useState(null);   // ExtractionResult id currently shown
  const [savedOpen, setSavedOpen] = useState(true);

  useEffect(() => {
    researchosService.getIngestDocuments(sourceType ? { source_type: sourceType } : {}).then((r) => setDocs(r.data || [])).catch(() => {});
  }, [sourceType]);

  const loadSaved = useCallback(() => {
    researchosService.getExtractionResults(sourceType ? { source_type: sourceType } : {}).then((r) => setSavedList(r.data || [])).catch(() => {});
  }, [sourceType]);
  useEffect(() => { loadSaved(); }, [loadSaved]);
  // refresh the saved list whenever a bulk run finishes or a manual preview is saved
  useEffect(() => { if (bulkPoll?.status === 'done' || saved?.saved) loadSaved(); }, [bulkPoll?.status, saved?.saved, loadSaved]);

  const openSaved = async (id) => {
    setBusy(true); setViewingSaved(id); setSaved(null); setJob(null); setSpkFilter(null); setPoll(null);
    try {
      const r = await researchosService.getExtractionResult(id);
      setPoll({ status: 'done', result: r.data.result });
    } catch (e) {
      setPoll({ status: 'failed', error: e?.response?.data?.error || e.message });
    } finally { setBusy(false); }
  };

  const stop = () => { if (timer.current) { clearInterval(timer.current); timer.current = null; } };

  const pollLoop = useCallback((jobId) => {
    stop();
    let misses = 0;
    // A 404 means the cache holds no entry for this job. With the dedicated `celery-research`
    // worker a real job flips to 'running' within ~1s, so a RUN of 404s means the job is GONE
    // (TTL-expired, or purged by a deploy/restart) — NOT merely queued. After EXPIRE_AFTER misses
    // (~36s) give up and tell the user, instead of showing "Queued" forever (the old behaviour,
    // which left a stale localStorage job_id spinning indefinitely).
    const EXPIRE_AFTER = 12;
    timer.current = setInterval(async () => {
      try {
        const r = await researchosService.pollExtraction(jobId);
        misses = 0;
        setPoll(r.data);
        if (r.data.status === 'done' || r.data.status === 'failed') {
          stop();
          if (r.data.status === 'done') localStorage.removeItem('extract_job');
        }
      } catch (e) {
        misses += 1;
        if (misses >= EXPIRE_AFTER) {
          stop();
          localStorage.removeItem('extract_job');   // don't resurrect a dead job on next refresh
          setPoll((p) => (p && p.status === 'done' ? p : { status: 'expired' }));
        } else {
          setPoll((p) => (p && p.status === 'done' ? p : { status: 'queued', misses }));
        }
      }
    }, 3000);
  }, []);
  useEffect(() => () => stop(), []);

  const stopBulk = () => { if (bulkTimer.current) { clearInterval(bulkTimer.current); bulkTimer.current = null; } };
  const bulkPollLoop = useCallback((jobId) => {
    stopBulk();
    let misses = 0;
    const EXPIRE_AFTER = 12;   // ~36s of 404s = the job is gone (TTL/deploy), not merely queued
    bulkTimer.current = setInterval(async () => {
      try {
        const r = await researchosService.pollBulkExtraction(jobId);
        misses = 0;
        setBulkPoll(r.data);
        if (r.data.status === 'done') { stopBulk(); localStorage.removeItem('bulk_job'); }
      } catch (e) {
        misses += 1;
        if (misses >= EXPIRE_AFTER) {
          stopBulk();
          localStorage.removeItem('bulk_job');
          setBulkPoll((p) => (p && p.status === 'done' ? p : { status: 'expired' }));
        }
      }
    }, 4000);
  }, []);
  useEffect(() => () => stopBulk(), []);

  // Resume a job across refreshes.
  useEffect(() => {
    const saved = localStorage.getItem('extract_job');
    if (saved) { setJob({ job_id: saved }); pollLoop(saved); }
    const savedBulk = localStorage.getItem('bulk_job');
    if (savedBulk) { setBulkJob({ job_id: savedBulk }); bulkPollLoop(savedBulk); }
  }, [pollLoop, bulkPollLoop]);

  const startBulk = async () => {
    setBulkBusy(true); setBulkErr(null); setBulkPoll(null); setBulkJob(null);
    try {
      const r = await researchosService.startBulkExtraction({ count: Number(bulkCount) || 50, order: bulkOrder, redo: bulkRedo, ...(sourceType ? { source_type: sourceType } : {}) });
      setBulkJob(r.data);
      localStorage.setItem('bulk_job', r.data.job_id);
      setBulkPoll({ status: 'running', total: r.data.doc_count, done: 0, committed: 0, skipped_gate: 0, errors: 0 });
      bulkPollLoop(r.data.job_id);
    } catch (e) {
      setBulkErr(e?.response?.data?.error || e.message);
    } finally { setBulkBusy(false); }
  };

  const start = async () => {
    if (!docId) return;
    setBusy(true); setPoll({ status: 'queued', misses: 0 }); setSaved(null); setJob(null); setViewingSaved(null);
    try {
      const r = await researchosService.startExtraction(Number(docId));
      setJob(r.data);
      localStorage.setItem('extract_job', r.data.job_id);
      pollLoop(r.data.job_id);
    } catch (e) {
      setPoll({ status: 'failed', error: e?.response?.data?.error || e.message });
    } finally { setBusy(false); }
  };

  const commit = async () => {
    if (!job) return;
    setBusy(true);
    try {
      const r = await researchosService.commitExtraction(job.job_id);
      setSaved(r.data);
    } catch (e) {
      setSaved({ error: e?.response?.data?.error || e.message });
    } finally { setBusy(false); }
  };

  const result = poll?.status === 'done' ? poll.result : null;
  const spkMap = useMemo(() => {
    const m = {};
    (result?.speakers || []).forEach((s, i) => { m[s.speaker_id] = { ...s, color: SPK_COLORS[i % SPK_COLORS.length] }; });
    return m;
  }, [result]);

  const claims = useMemo(() => {
    let c = result?.claims || [];
    if (spkFilter) c = c.filter((x) => x.speaker_id === spkFilter);
    return [...c].sort((a, b) => (b.confidence || 0) - (a.confidence || 0));
  }, [result, spkFilter]);
  // topic_id → topic (title + themes). Claims now carry topic_id (structural parent), not a topic string.
  const topicById = useMemo(() => {
    const m = {};
    (result?.topics || []).forEach((t) => { m[t.id] = t; });
    return m;
  }, [result]);

  return (
    <div>
      {/* ── run bar ── */}
      <div className="mb-4 rounded-lg border border-gray-200 p-4 dark:border-gray-800">
        <div className="mb-2 text-[11px] uppercase tracking-wider text-gray-500">Run extraction (DeepSeek) — preview only</div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-[280px] flex-1">
            <label className="text-xs text-gray-500">Transcript</label>
            <select value={docId} onChange={(e) => setDocId(e.target.value)}
              className="mt-1 block w-full rounded border border-gray-300 bg-transparent px-2 py-1.5 text-sm dark:border-gray-700 dark:bg-gray-900">
              <option value="">— pick a transcript —</option>
              {docs.map((d) => (
                <option key={d.id} value={d.id}>{(d.source_ref || '?')} · {(d.title || '').slice(0, 70)}</option>
              ))}
            </select>
          </div>
          <button onClick={start} disabled={busy || !docId || poll?.status === 'running'}
            className="rounded px-4 py-1.5 text-sm font-medium disabled:opacity-50"
            style={{ color: GOLD, border: `1px solid ${GOLD}` }}>
            {poll?.status === 'running' ? 'Running…' : 'Run extraction'}
          </button>
        </div>
        <p className="mt-2 text-[11px] text-gray-400">
          Nothing is written to the database. The result is held in cache for review; use <b>Save to DB</b> below only if you approve it.
        </p>
      </div>

      {/* ── bulk extract → auto-commit (earliest-first, grounding-gated, NO per-video review) ── */}
      <div className="mb-4 rounded-lg border border-gray-200 p-4 dark:border-gray-800">
        <div className="mb-2 text-[11px] uppercase tracking-wider text-gray-500">Bulk extract → auto-commit</div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="text-xs text-gray-500">How many</label>
            <input type="number" min="1" max="500" value={bulkCount}
              onChange={(e) => setBulkCount(e.target.value)}
              className="mt-1 block w-24 rounded border border-gray-300 bg-transparent px-2 py-1.5 text-sm dark:border-gray-700 dark:bg-gray-900" />
          </div>
          <div>
            <label className="text-xs text-gray-500">Order</label>
            <select value={bulkOrder} onChange={(e) => setBulkOrder(e.target.value)}
              className="mt-1 block rounded border border-gray-300 bg-transparent px-2 py-1.5 text-sm dark:border-gray-700 dark:bg-gray-900">
              <option value="earliest">Earliest first</option>
              <option value="latest">Latest first</option>
            </select>
          </div>
          <label className="flex items-center gap-1.5 pb-2 text-xs text-gray-600 dark:text-gray-300" title="Re-extract documents that already have an extraction (their text changed — e.g. X quoted text / full long-form text arrived after the first pass). Adds a new version; reads use the latest.">
            <input type="checkbox" checked={bulkRedo} onChange={(e) => setBulkRedo(e.target.checked)} /> redo already-extracted
          </label>
          <button onClick={startBulk} disabled={bulkBusy || bulkPoll?.status === 'running'}
            className="rounded px-4 py-1.5 text-sm font-medium disabled:opacity-50"
            style={{ color: GOLD, border: `1px solid ${GOLD}` }}>
            {bulkPoll?.status === 'running' ? 'Running…' : (bulkRedo ? 'Run bulk extract (redo)' : 'Run bulk extract')}
          </button>
        </div>
        <p className="mt-2 text-[11px] text-gray-400">
          Extracts un-extracted transcripts one at a time (<b>earliest-first</b> builds the graph in order) and
          <b> auto-commits</b> each that clears the grounding gate — no per-video review. Already-committed docs are
          skipped, so a re-run resumes. <span className="text-amber-600 dark:text-amber-400">Don’t deploy while a bulk run is in progress</span> (it recreates the worker).
        </p>
        {bulkErr && <div className="mt-2 text-xs text-red-500">{bulkErr}</div>}
        {bulkPoll && bulkPoll.status !== 'expired' && (
          <div className="mt-3">
            <div className="mb-1 flex items-center justify-between text-[11px] text-gray-500">
              <span>{bulkPoll.status === 'done' ? 'Done' : 'Extracting…'} {bulkPoll.done || 0}/{bulkPoll.total || 0}</span>
              {typeof bulkPoll.cost_usd === 'number' && <span>${(bulkPoll.cost_usd || 0).toFixed(4)}</span>}
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded bg-gray-100 dark:bg-gray-800">
              <div className="h-full rounded transition-all"
                style={{ width: `${bulkPoll.total ? Math.round(100 * (bulkPoll.done || 0) / bulkPoll.total) : 0}%`, background: GOLD }} />
            </div>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
              <span className="text-green-600 dark:text-green-400">✓ {bulkPoll.committed || 0} committed</span>
              <span className="text-gray-500">↓ {bulkPoll.skipped_gate || 0} below gate</span>
              <span className="text-red-500">✕ {bulkPoll.errors || 0} errors</span>
            </div>
          </div>
        )}
        {bulkPoll?.status === 'expired' && (
          <div className="mt-2 text-xs text-gray-500">Previous bulk run expired or was cleared (e.g. by a deploy). Re-run to resume — committed docs are skipped.</div>
        )}
      </div>

      {/* ── saved / committed extractions — click to view with the full preview UI ── */}
      <div className="mb-4 rounded-lg border border-gray-200 dark:border-gray-800">
        <button onClick={() => setSavedOpen((v) => !v)} className="flex w-full items-center justify-between px-4 py-3 text-left">
          <span className="text-[11px] uppercase tracking-wider text-gray-500">Saved extractions <span className="ml-1 text-gray-400">{savedList.length}</span></span>
          <span className="text-xs text-gray-400">{savedOpen ? '▾' : '▸'}</span>
        </button>
        {savedOpen && (
          <div className="max-h-64 overflow-y-auto border-t border-gray-100 dark:border-gray-800">
            {savedList.length === 0 ? (
              <p className="px-4 py-3 text-xs text-gray-400">No saved extractions yet — run a bulk extract or save a manual preview.</p>
            ) : savedList.map((s) => (
              <button key={s.id} onClick={() => openSaved(s.id)}
                className={`flex w-full items-center gap-3 border-b border-gray-50 px-4 py-2 text-left text-xs hover:bg-gray-50 dark:border-gray-800/60 dark:hover:bg-gray-800/40 ${viewingSaved === s.id ? 'bg-amber-50/60 dark:bg-amber-900/10' : ''}`}>
                <span className="min-w-0 flex-1 truncate">
                  <span className="text-gray-500">{s.source_ref || '?'}</span>{' · '}
                  <span className="text-gray-800 dark:text-gray-200">{(s.title || '').slice(0, 70) || '(untitled)'}</span>
                </span>
                <span className="whitespace-nowrap tabular-nums text-gray-400">
                  {s.grounding_rate != null ? `${Math.round(s.grounding_rate * 100)}%` : '—'} · {s.claim_count} claims
                </span>
                <span className="whitespace-nowrap text-gray-400">{s.published_at ? new Date(s.published_at).toLocaleDateString() : ''}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── queued / running / failed ── */}
      {poll?.status === 'queued' && (
        <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm dark:border-gray-800 dark:bg-gray-900/40">
          <span className="text-gray-600 dark:text-gray-300">Queued — waiting for a worker to pick it up… (this is normal; it will start shortly)</span>
        </div>
      )}
      {poll?.status === 'running' && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm dark:border-amber-900/40 dark:bg-amber-900/10">
          <div className="mb-1 font-medium text-amber-700 dark:text-amber-300">Extracting… (this can take a few minutes)</div>
          <div className="max-h-32 overflow-y-auto font-mono text-[11px] text-gray-500">
            {(poll.log || []).map((l, i) => <div key={i}>{l}</div>)}
          </div>
        </div>
      )}
      {poll?.status === 'failed' && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/10 dark:text-red-300">
          Extraction failed: {poll.error}
        </div>
      )}
      {poll?.status === 'expired' && (
        <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm dark:border-gray-800 dark:bg-gray-900/40">
          <span className="text-gray-600 dark:text-gray-300">Previous preview expired or was cleared (e.g. by a deploy) — pick a transcript and run again.</span>
        </div>
      )}

      {/* ── result ── */}
      {result && (
        <div>
          {/* metrics + cost + save */}
          <div className="mb-4 rounded-lg border border-gray-200 dark:border-gray-800">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 p-4">
              <Metric label="Grounding" value={`${(result.metrics.grounding_rate * 100).toFixed(0)}%`}
                color={result.grounding_gate_ok ? '#5fbf8f' : '#e0705f'}
                note={(() => {
                  // Gate threshold comes from the backend (metrics.grounding_gate) — never hardcoded,
                  // so the label can't drift from the enforced gate. Fallback 90% for old cached blobs.
                  const gatePct = Math.round((result.metrics.grounding_gate ?? 0.90) * 100);
                  return result.grounding_gate_ok ? `gate ✓ ≥${gatePct}%` : `below ${gatePct}% gate`;
                })()} />
              <Metric label="Claims" value={result.metrics.claim_count}
                note={`${result.metrics.claim_density_per_1k}/1k words · ${result.metrics.unverified_count ?? 0} unverified`} />
              <Metric label="Cost" value={`$${result.cost.usd.toFixed(4)}`}
                note={`${result.cost.tokens_in.toLocaleString()} in / ${result.cost.tokens_out.toLocaleString()} out · ${result.cost.calls} calls · ${result.cost.model}`} />
              <div className="ml-auto flex items-center gap-2">
                {viewingSaved ? (
                  <span className="text-sm text-gray-500 dark:text-gray-400">Saved extraction #{viewingSaved}</span>
                ) : saved?.saved ? (
                  <span className="text-sm text-green-600 dark:text-green-400">✓ Saved (#{saved.id})</span>
                ) : (
                  <button onClick={commit} disabled={busy}
                    className="rounded px-4 py-1.5 text-sm font-medium text-white disabled:opacity-50"
                    style={{ background: GOLD, color: '#111' }}>
                    {busy ? 'Saving…' : 'Save to DB'}
                  </button>
                )}
              </div>
            </div>
            {saved?.error && <div className="border-t border-gray-100 px-4 py-2 text-xs text-red-500 dark:border-gray-800">{saved.error}</div>}
          </div>

          {/* thesis + topics */}
          {(result.thesis || (result.topics || []).length > 0) && (
            <div className="mb-4 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900/40">
              {result.thesis && <p className="mb-3 font-serif text-[15px] leading-relaxed text-gray-800 dark:text-gray-100">{result.thesis}</p>}
              {(result.topics || []).length > 0 && (
                <div className="flex flex-col gap-2">
                  <div className="mb-0.5 text-[10px] font-medium uppercase tracking-wider" style={{ color: GOLD }}>
                    Topic tree — {result.topics.length} topics · each point → its companies (stance) → its claims
                  </div>
                  {result.topics.map((t, i) => (
                    <div key={i} className="border-l-2 pl-3" style={{ borderColor: GOLD }}>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{t.title}</span>
                        {(t.sectors || []).map((s, j) => <span key={j} className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500 dark:bg-gray-800 dark:text-gray-400">{s}</span>)}
                      </div>
                      {t.summary && <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{t.summary}</p>}
                      {(t.entities || []).length > 0 ? (
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {t.entities.map((e, k) => <StanceChip key={k} e={e} />)}
                        </div>
                      ) : (
                        <div className="mt-1.5 text-[10px] italic text-gray-400">— no companies tagged on this topic (re-run — this build adds per-topic stance) —</div>
                      )}
                      {(result.claims || []).some((c) => c.topic_id === t.id) && (
                        <div className="mt-2 flex flex-col gap-1">
                          {(result.claims || []).filter((c) => c.topic_id === t.id).map((c, k) => (
                            <div key={k} className="pl-2 text-xs text-gray-600 dark:text-gray-300"
                              style={{ borderLeft: `2px solid ${c.direction === 'bullish' ? '#16a34a' : c.direction === 'bearish' ? '#dc2626' : '#9ca3af'}` }}>
                              {c.canonical_claim}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* tabs */}
          <nav className="mb-3 flex flex-wrap gap-1">
            {[['claims', `Claims ${result.claims.length}`],
              ['relationships', `Relationships ${(result.relationships || []).length}`],
              ['stance', `Debates ${result.propositions.length}`],
              ['entities', `Entities ${result.entities.length}`], ['cost', 'Cost']].map(([id, lab]) => (
              <button key={id} onClick={() => setTab(id)}
                className="rounded px-3 py-1.5 text-sm"
                style={tab === id ? { color: GOLD, border: `1px solid ${GOLD}`, fontWeight: 500 }
                  : { border: '1px solid transparent', color: '#6b7280' }}>{lab}</button>
            ))}
          </nav>

          {/* CLAIMS */}
          {tab === 'claims' && (
            <div>
              <div className="mb-2 flex flex-wrap gap-1">
                <button onClick={() => setSpkFilter(null)} className="rounded px-2 py-0.5 text-xs"
                  style={!spkFilter ? { color: GOLD, border: `1px solid ${GOLD}` } : { color: '#6b7280', border: '1px solid transparent' }}>All</button>
                {Object.values(spkMap).map((s) => (
                  <button key={s.speaker_id} onClick={() => setSpkFilter(s.speaker_id)} className="rounded px-2 py-0.5 text-xs"
                    style={spkFilter === s.speaker_id ? { color: s.color, border: `1px solid ${s.color}` } : { color: '#6b7280', border: '1px solid transparent' }}>
                    {s.name || s.speaker_id}
                  </button>
                ))}
              </div>
              <div className="flex flex-col gap-2">
                {claims.map((c) => {
                  const s = spkMap[c.speaker_id] || { color: '#8b95a6', name: c.speaker_id };
                  return (
                    <div key={c.claim_id} className="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-800 dark:bg-gray-900/40"
                      style={{ borderLeft: `3px solid ${s.color}` }}>
                      {/* Topic breadcrumb — the claim's context anchor. Every claim belongs to one
                          distilled topic; showing it keeps the claim from reading as an orphaned
                          fragment and groups sibling claims under the same subject. */}
                      {topicById[c.topic_id] && (
                        <div className="mb-1 flex flex-wrap items-center gap-1.5">
                          <span className="text-[10px] font-medium uppercase tracking-wide" style={{ color: 'rgb(var(--brand-gold-rgb))' }}>{topicById[c.topic_id].title}</span>
                          {(topicById[c.topic_id].sectors || []).map((s, j) => <span key={j} className="rounded bg-gray-100 px-1 py-0.5 text-[9px] uppercase tracking-wide text-gray-500 dark:bg-gray-800 dark:text-gray-400">{s}</span>)}
                        </div>
                      )}
                      <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
                        <span className="text-[12px] font-bold uppercase tracking-wide" style={{ color: s.color }}>{s.name || c.speaker_id}</span>
                        <Pill>{c.claim_type}</Pill>
                        {c.modality && c.modality !== 'assertion' && <Pill color="#d8a24a">{c.modality}</Pill>}
                        {c.falsifiable && <Pill color="#5fbf8f">falsifiable</Pill>}
                        {c.self_serving && <Pill color="#d8a24a">self-serving</Pill>}
                        {(c.flags || []).includes('against_interest') && <Pill color="#5fbf8f">against-interest</Pill>}
                        {c.verdict && c.verdict !== 'SUPPORTED' && <Pill color={verdictStyle[c.verdict]}>{c.verdict}</Pill>}
                        <span className="ml-auto font-mono text-[10px] text-gray-400">conf {(c.confidence ?? 0).toFixed(2)} · {c.claim_id}</span>
                      </div>
                      <p className="mb-1.5 text-sm text-gray-800 dark:text-gray-100">{c.canonical_claim}</p>
                      {c.evidence_quote && (
                        <p className="border-l-2 pl-2 text-xs italic text-gray-500" style={{ borderColor: '#8f7a4e' }}>
                          “{c.evidence_quote}”
                          {c.source_span && <span className="ml-1 font-mono not-italic text-[10px] text-gray-400">[{c.source_span.start}–{c.source_span.end}]</span>}
                        </p>
                      )}
                      {(c.subject_entities || []).length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {c.subject_entities.map((e, i) => <span key={i} className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-600 dark:bg-gray-800 dark:text-gray-300">{e}</span>)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* RELATIONSHIPS */}
          {tab === 'relationships' && (
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-gray-200 dark:border-gray-800">
                  {['Subject', 'Relation', 'Object', 'Note'].map((h) => <th key={h} className="p-2 text-left text-[10px] uppercase tracking-wider text-gray-400">{h}</th>)}
                </tr></thead>
                <tbody>
                  {(result.relationships || []).map((r, i) => (
                    <tr key={i} className="border-b border-gray-100 dark:border-gray-800/50">
                      <td className="p-2 font-mono text-xs text-gray-800 dark:text-gray-100">{r.subject}</td>
                      <td className="p-2"><Pill color={GOLD}>{r.relation}</Pill></td>
                      <td className="p-2 font-mono text-xs text-gray-800 dark:text-gray-100">{r.object}</td>
                      <td className="p-2 text-xs text-gray-500">{r.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* DEBATES (stance) */}
          {tab === 'stance' && (
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-gray-200 dark:border-gray-800">
                  <th className="p-2 text-left text-[10px] uppercase tracking-wider text-gray-400">Proposition</th>
                  {Object.values(spkMap).map((s) => <th key={s.speaker_id} className="p-2 text-center text-[10px] uppercase" style={{ color: s.color }}>{(s.name || s.speaker_id).split(' ').slice(-1)[0]}</th>)}
                  <th className="p-2 text-left text-[10px] uppercase tracking-wider text-gray-400">Note</th>
                </tr></thead>
                <tbody>
                  {result.propositions.map((p, i) => (
                    <tr key={i} className="border-b border-gray-100 dark:border-gray-800/50">
                      <td className="p-2 align-top">
                        <div className="text-gray-800 dark:text-gray-100">{p.proposition}</div>
                        {p.convergence && <span className="mt-0.5 inline-block text-[10px] font-bold uppercase text-green-600 dark:text-green-400">convergence</span>}
                        <div className="font-mono text-[10px] text-gray-400">orig: {spkMap[p.originator_speaker_id]?.name || p.originator_speaker_id}</div>
                      </td>
                      {Object.values(spkMap).map((s) => {
                        const v = (p.stances || {})[s.speaker_id] || 'none';
                        const st = stanceStyle[v] || stanceStyle.none;
                        return <td key={s.speaker_id} className="p-2 text-center text-[11px] font-bold" style={{ color: st.c }}>{st.t}</td>;
                      })}
                      <td className="p-2 align-top text-xs text-gray-500">{p.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* TENSIONS */}
          {tab === 'tensions' && (
            <div className="flex flex-col gap-2">
              {(result.derived?.tensions || []).map((t, i) => (
                <div key={i} className="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-800 dark:bg-gray-900/40"
                  style={{ borderLeft: `3px ${t.model_supplied ? 'dashed' : 'solid'} ${t.model_supplied ? '#726f68' : '#e0705f'}` }}>
                  <div className="mb-1 flex items-center gap-2">
                    <Pill>{t.kind}</Pill>
                    {t.model_supplied ? <Pill color="#726f68">model-generated</Pill> : <Pill color="#5fbf8f">in document</Pill>}
                  </div>
                  <p className="text-sm text-gray-800 dark:text-gray-100">{t.detail}</p>
                  {(t.claim_ids || []).length > 0 && <p className="mt-1 font-mono text-[10px] text-gray-400">{(t.claim_ids || []).join(' · ')}</p>}
                </div>
              ))}
              {(result.derived?.tensions || []).length === 0 && <p className="text-sm text-gray-400">No tensions found.</p>}
            </div>
          )}

          {/* DILIGENCE */}
          {tab === 'queue' && (
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-gray-200 dark:border-gray-800">
                  {['Pri', 'Task', 'Resolves', 'Effort', 'Data'].map((h) => <th key={h} className="p-2 text-left text-[10px] uppercase tracking-wider text-gray-400">{h}</th>)}
                </tr></thead>
                <tbody>
                  {(result.derived?.diligence || []).map((q, i) => (
                    <tr key={i} className="border-b border-gray-100 dark:border-gray-800/50">
                      <td className="p-2"><Pill solid={q.priority === 'P0'} color={GOLD}>{q.priority}</Pill></td>
                      <td className="p-2 text-gray-800 dark:text-gray-100">{q.task}</td>
                      <td className="p-2 font-mono text-[10px] text-gray-400">{q.resolves}</td>
                      <td className="p-2 font-mono text-[11px] text-gray-500">{q.effort}</td>
                      <td className="p-2 font-mono text-[11px] text-gray-500">{q.data}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ENTITIES — default to the companies shortlist (the mapping targets); toggle to show all */}
          {tab === 'entities' && (() => {
            const allEnts = result.entities || [];
            // Back-compat: previews from before is_company shipped → treat every entity as shown.
            const hasFlag = allEnts.some((e) => e.is_company !== undefined);
            const companyEnts = hasFlag ? allEnts.filter((e) => e.is_company) : allEnts;
            const shown = showAllEnts ? allEnts : companyEnts;
            const publicN = companyEnts.filter((e) => e.status === 'RESOLVED').length;
            return (
              <div>
                <div className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] uppercase tracking-wider text-gray-500">
                  <span>{companyEnts.length} companies · <span className="text-green-600 dark:text-green-400">{publicN} public</span> (ticker + GICS)</span>
                  <span className="normal-case tracking-normal text-gray-400">— ✓ us-listed · ⚠ guess · "not covered" = we don't cover it (may be private OR foreign-listed)</span>
                  {hasFlag && allEnts.length > companyEnts.length && (
                    <button onClick={() => setShowAllEnts((v) => !v)}
                      className="ml-auto rounded border border-gray-300 px-1.5 py-0.5 text-[10px] normal-case tracking-normal text-gray-500 hover:text-gray-700 dark:border-gray-700 dark:hover:text-gray-300">
                      {showAllEnts ? 'Companies only' : `Show all ${allEnts.length} (incl. people/products)`}
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                  {shown.map((e, i) => (
                    <div key={i} className="flex items-center gap-2 rounded border border-gray-200 px-2.5 py-1.5 text-sm dark:border-gray-800"
                      title={e.status === 'RESOLVED' ? `${e.ticker} · ${e.company || ''} · ${e.sector || ''}${e.sub_industry_name ? ' / ' + e.sub_industry_name : ''}` : (e.ticker_guess ? `unverified guess: ${e.ticker_guess}` : '')}>
                      {e.is_company === false
                        ? <span className="text-gray-300 dark:text-gray-600" title={`${e.type} — not a mapping target`}>·</span>
                        : <span className={e.status === 'RESOLVED' ? 'text-green-500' : 'text-amber-500'}>{e.status === 'RESOLVED' ? '✓' : '⚠'}</span>}
                      <span className="text-gray-800 dark:text-gray-100">{e.canonical || e.surface}</span>
                      {e.stance && <span className="text-[11px] font-bold" style={{ color: stanceOf(e.stance).c }} title={`net stance: ${e.stance}`}>{stanceOf(e.stance).m}</span>}
                      {e.status === 'RESOLVED'
                        ? (<>
                            <span className="rounded bg-gray-100 px-1 py-0.5 font-mono text-[10px] text-gray-600 dark:bg-gray-800 dark:text-gray-300">{e.ticker}</span>
                            {e.sector && <span className="text-[10px] text-gray-400">{e.sector}</span>}
                          </>)
                        : (e.is_company !== false
                            ? (e.ticker_guess
                                ? <span className="text-[10px] text-amber-500/70">guess {e.ticker_guess}?</span>
                                : <span className="text-[10px] text-gray-400">not covered</span>)
                            : null)}
                      <span className="ml-auto font-mono text-[10px] text-gray-400">{e.type}</span>
                    </div>
                  ))}
                  {shown.length === 0 && <p className="text-sm text-gray-400">No {showAllEnts ? 'entities' : 'companies'} found.</p>}
                </div>
              </div>
            );
          })()}

          {/* COST */}
          {tab === 'cost' && (
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-gray-200 dark:border-gray-800">
                  {['Pass', 'Calls', 'Tokens in', 'Tokens out'].map((h) => <th key={h} className="p-2 text-left text-[10px] uppercase tracking-wider text-gray-400">{h}</th>)}
                </tr></thead>
                <tbody>
                  {Object.entries(result.cost.by_pass || {}).map(([p, v]) => (
                    <tr key={p} className="border-b border-gray-100 dark:border-gray-800/50">
                      <td className="p-2 font-mono text-xs text-gray-700 dark:text-gray-300">{p}</td>
                      <td className="p-2 font-mono text-xs tabular-nums">{v.calls}</td>
                      <td className="p-2 font-mono text-xs tabular-nums">{v.tokens_in.toLocaleString()}</td>
                      <td className="p-2 font-mono text-xs tabular-nums">{v.tokens_out.toLocaleString()}</td>
                    </tr>
                  ))}
                  <tr className="font-medium">
                    <td className="p-2 text-xs">TOTAL · {result.cost.model}</td>
                    <td className="p-2 font-mono text-xs tabular-nums">{result.cost.calls}</td>
                    <td className="p-2 font-mono text-xs tabular-nums">{result.cost.tokens_in.toLocaleString()}</td>
                    <td className="p-2 font-mono text-xs tabular-nums">{result.cost.tokens_out.toLocaleString()}</td>
                  </tr>
                </tbody>
              </table>
              <div className="border-t border-gray-100 px-3 py-2 text-xs text-gray-500 dark:border-gray-800">
                <b>${result.cost.usd.toFixed(4)}</b> · {result.cost.cache_hit_pct != null ? `${result.cost.cache_hit_pct}% of input was cache-hit` : ''}
                {' '}· ${result.cost.price_in_per_m}/M in · ${result.cost.price_cached_per_m ?? '—'}/M cached · ${result.cost.price_out_per_m}/M out.
                Set CHEAP_LLM_PRICE_* env to your exact contract (incl. off-peak) for an exact figure.
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, note, color }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-gray-400">{label}</div>
      <div className="font-serif text-xl" style={{ color: color || undefined }}>{value}</div>
      {note && <div className="text-[11px] text-gray-400">{note}</div>}
    </div>
  );
}
